import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useHaptic } from '@/hooks/useHaptic';

type Variant = 'primary' | 'secondary' | 'destructive' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  fullWidth?: boolean;
  children: ReactNode;
}

/**
 * Универсальная кнопка в стиле Telegram. Большой тач-таргет (минимум 44px),
 * акцентный цвет берётся из темы, тактильная отдача при нажатии.
 */
export function Button({
  variant = 'primary',
  fullWidth,
  children,
  onClick,
  style,
  ...rest
}: ButtonProps): JSX.Element {
  const { impact } = useHaptic();

  const base: React.CSSProperties = {
    minHeight: 'var(--tap-target)',
    padding: '0 var(--space-lg)',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    fontSize: 'var(--font-md)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity .15s, transform .05s',
    width: fullWidth ? '100%' : 'auto',
  };

  const variants: Record<Variant, React.CSSProperties> = {
    primary: { background: 'var(--tg-button)', color: 'var(--tg-button-text)' },
    secondary: { background: 'var(--tg-secondary-bg)', color: 'var(--tg-text)' },
    destructive: { background: 'transparent', color: 'var(--tg-destructive)' },
    ghost: { background: 'transparent', color: 'var(--tg-link)' },
  };

  return (
    <button
      {...rest}
      onClick={(e) => {
        impact('light');
        onClick?.(e);
      }}
      style={{ ...base, ...variants[variant], ...style }}
      onMouseDown={(e) => (e.currentTarget.style.opacity = '0.7')}
      onMouseUp={(e) => (e.currentTarget.style.opacity = '1')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
    >
      {children}
    </button>
  );
}
